import React, { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import api from '../lib/api';
import PersonHero from '../components/person/PersonHero';
import LegislativeRecord from '../components/person/LegislativeRecord';
import CandidateRace, { Endorsements, MeasurePositions, OwnWords } from '../components/person/CandidateRace';
import CampaignFinanceMultiYear from '../components/person/CampaignFinanceMultiYear';
import FederalCampaignFinance from '../components/person/FederalCampaignFinance';
import { candidateOffice } from '../components/person/personDisplay';
import NoteCountBadge from '../components/notes/NoteCountBadge';
import CustomFieldBadge from '../components/custom-fields/CustomFieldBadge';
import LegislatorWorkspaceSelectionModal from '../components/LegislatorWorkspaceSelectionModal';
import CandidateWorkspaceSelectionModal from '../components/CandidateWorkspaceSelectionModal';
import Button from '../components/Button';
import { TrackIcon } from '../components/icons';
import { isFederalFinance, type PersonData } from '../components/person/types';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import PageShell from '../components/PageShell';

interface PersonDetailPageProps {
  isPublic?: boolean;
  person?: PersonData;
}

const PersonDetailPage: React.FC<PersonDetailPageProps> = ({ isPublic = false, person: externalPerson }) => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [trackTarget, setTrackTarget] = useState<'legislator' | 'candidate' | null>(null);

  const urlParams = new URLSearchParams(location.search);
  const fromWorkspace = !isPublic ? urlParams.get('workspace') : null;

  const typeHint = location.pathname.startsWith('/candidates') ? 'candidate'
    : location.pathname.startsWith('/legislators') || location.pathname.startsWith('/citizen/legislators') ? 'legislator'
    : undefined;

  const { data: fetchedPerson, isLoading, error } = useQuery<PersonData>(
    ['person', id, typeHint],
    async () => {
      const params = typeHint ? `?type=${typeHint}` : '';
      const res = await api.get(`/people/${id}${params}`);
      return res.data.data;
    },
    { enabled: !isPublic && !!id }
  );

  const person = isPublic ? externalPerson : fetchedPerson;

  if (!isPublic && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if ((!isPublic && error) || !person) {
    return (
      <div className="p-6 text-center">
        <p className="text-text-secondary dark:text-text-dark-secondary">
          {error ? 'Failed to load person details' : 'Person not found'}
        </p>
        {!isPublic && (
          <Button onClick={() => navigate(-1)} variant="secondary" size="sm" icon={ArrowLeft} className="mt-4">
            Go back
          </Button>
        )}
      </div>
    );
  }

  const isLeg = person.roles.includes('legislator') && Boolean(person.legislator);
  const isCand = person.roles.includes('candidate') && Boolean(person.candidate);

  /*
    Pro actions, in the hero. Notes and custom fields keep today's rule — shown
    only when arrived from a workspace, which is what scopes them. Track is
    always offered for the roles that can be tracked: a legislator and a
    candidacy are tracked separately, so a sitting legislator who is also
    running gets both, the candidacy as the primary action.
  */
  const actions = isPublic ? null : (
    <>
      {fromWorkspace && isLeg && (
        <NoteCountBadge
          entityType="legislator"
          entityId={String(person.legislator?.id || '')}
          workspaceId={fromWorkspace}
          showNotesOnClick
          variant="button"
        />
      )}
      {fromWorkspace && isCand && (
        <CustomFieldBadge
          entityType="candidate"
          entityId={person.candidate?.id || ''}
          entityDisplayName={person.full_name}
          workspaceId={fromWorkspace}
          canEdit
          variant="button"
        />
      )}
      {isLeg && (
        <Button
          variant={isCand ? 'secondary' : 'primary'}
          size="sm"
          icon={TrackIcon}
          onClick={() => setTrackTarget('legislator')}
        >
          {isCand ? 'Track legislator' : 'Track'}
        </Button>
      )}
      {isCand && (
        <Button variant="primary" size="sm" icon={TrackIcon} onClick={() => setTrackTarget('candidate')}>
          {isLeg ? 'Track candidate' : 'Track'}
        </Button>
      )}
    </>
  );
  const hasActions = !isPublic && (isLeg || isCand || Boolean(fromWorkspace));

  const endorsementsShown = isCand && (person.candidate?.endorsements?.length ?? 0) > 0;

  const body = (
    <div className="space-y-5">
      <PersonHero person={person} actions={hasActions ? actions : undefined} />

      {/* Reading order (2026-10 redesign, matching the measure page): the race
          and the candidate's positions, then the record in office, then
          endorsements beside the money, and the path to the ballot last. The
          design's Official record (filings, prior offices) is omitted: the
          payload carries neither yet. */}
      {isCand && <CandidateRace candidate={person.candidate!} isPublic={isPublic} />}
      {isCand && <OwnWords statements={person.candidate!.statements ?? []} />}
      {isCand && <MeasurePositions positions={person.candidate!.measure_positions ?? []} isPublic={isPublic} />}
      {isLeg && <LegislativeRecord legislator={person.legislator!} isPublic={isPublic} />}

      <div className={endorsementsShown ? 'grid grid-cols-1 items-start gap-5 xl:grid-cols-2' : ''}>
        {endorsementsShown && <Endorsements candidate={person.candidate!} />}
        {isFederalFinance(person.campaign_finance) ? (
          <FederalCampaignFinance
            finance={person.campaign_finance}
            campaignLabel={isCand ? `${candidateOffice(person.candidate!)} campaign` : null}
          />
        ) : (
          <CampaignFinanceMultiYear
            finance={person.campaign_finance}
            personName={person.full_name}
            electionYear={isCand ? person.candidate!.election_year : null}
            campaignLabel={isCand ? `${candidateOffice(person.candidate!)} campaign` : null}
            isPublic={isPublic}
          />
        )}
      </div>

      <div className="pt-6 pb-12 text-center text-xs text-slate-400 dark:text-text-dark-tertiary">
        Data updated daily from Secretary of State filings · Capitol Commons
      </div>

      {trackTarget === 'legislator' && person.legislator && (
        <LegislatorWorkspaceSelectionModal
          isOpen
          onClose={() => setTrackTarget(null)}
          legislatorId={String(person.legislator.id)}
          legislatorName={person.full_name}
        />
      )}
      {trackTarget === 'candidate' && person.candidate && (
        <CandidateWorkspaceSelectionModal
          isOpen
          onClose={() => setTrackTarget(null)}
          candidateId={person.candidate.id}
          candidateName={person.full_name}
        />
      )}
    </div>
  );

  /*
    The public route renders this component outside Layout, where there is no
    global header to register with — so only the authenticated case is shelled.
  */
  return isPublic ? body : <PageShell title={person.full_name}>{body}</PageShell>;
};

export default PersonDetailPage;
